import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SupportService } from '../../../core/services/support.service';
import { AlertService } from '../../../core/services/alert.service';
import { AppStateService } from '../../../core/services/app-state.service';
import { UploadResilienceService } from '../../../core/services/upload-resilience.service';
import { supportErrorKey } from '../../../core/services/support-errors';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { CartPhotosBlockedDialogComponent } from '../../../shared/components/cart-photos-blocked-dialog/cart-photos-blocked-dialog.component';
import { ProjectPickerDialogComponent, ProjectPickerDialogData } from '../project-picker-dialog/project-picker-dialog.component';

/**
 * Subset of `product.projectInfo` the menu cares about. The full backend
 * payload has more fields (editorTypeId, productName, …) — only what the
 * menu reads is typed here. Falsy or missing means the order item has no
 * associated project and the menu shouldn't render at all (the parent
 * gates on this).
 */
export interface OrderItemProjectInfo {
  id: number;
  editorTypeId?: number;
  productName?: string;
}

type RepairAction = 'update' | 'replace' | 'edit';

/**
 * Per-item support menu surfaced inside an order's expanded view. Replaces
 * the per-group amber toolbar (PR 5) with a single icon button that opens
 * a Material menu — three actions matching the spec:
 *
 *  - Editar (PR 8): opens the editor against the ordered .dbproject in
 *    the orders folder; saves go through replaceOrderItemProject. Hidden
 *    in PR 7 because the editor branch isn't wired yet — see the
 *    `<!-- Editar -->` block in the template.
 *  - Atualizar: re-syncs the ordered file from the customer's *current*
 *    project (PUT /support/order-items/:id/project, no body).
 *  - Substituir: opens the candidate-project picker, then PUTs the
 *    selected project id (PUT /support/order-items/:id/project/:projectId).
 *
 * Visibility is decided by the parent (OrderDetailsDialog), not here —
 * `canRepairOrderItems && product.projectInfo`. Keeping the gate at the
 * call site avoids duplicating the staff/impersonation logic.
 */
@Component({
  selector: 'app-order-item-repair-menu',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './order-item-repair-menu.component.html',
  styleUrl: './order-item-repair-menu.component.scss'
})
export class OrderItemRepairMenuComponent {
  private readonly supportService = inject(SupportService);
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly appState = inject(AppStateService);
  private readonly uploadResilience = inject(UploadResilienceService);

  @Input({ required: true }) orderItemId!: number;
  @Input({ required: true }) projectInfo!: OrderItemProjectInfo;

  /**
   * The order's backend API version. apiVersion === 1 marks a legacy order
   * created by the old app; its project files aren't editable/repairable
   * here, so every action is intercepted with an info dialog pointing staff
   * to the respective (legacy) app. Anything else (incl. undefined) is
   * treated as a current order.
   */
  @Input() apiVersion?: number;

  /** Parent re-fetches the order on success so the visible state reflects the change. */
  @Output() repaired = new EventEmitter<void>();

  /** Tracks which action is in flight; drives per-item spinners + disabled state. */
  readonly busy = signal<RepairAction | null>(null);

  // ── Editar ────────────────────────────────────────────────

  /**
   * Opens the editor on the *ordered* .dbproject. The editor calls
   * `GET /support/order-items/:id/project` to resolve both the file URL
   * and the linked project's id, so navigation needs only one query
   * param. URL is fully bookmarkable + refresh-resilient.
   */
  onEditClick(): void {
    if (this.guardLegacyOrder()) return;
    // Close any open Material dialogs (notably the OrderDetailsDialog
    // this menu is mounted inside) so the editor renders without a
    // modal stacked on top of it.
    this.dialog.closeAll();
    const locale = this.appState.locale() || 'pt_PT';
    this.router.navigate(['/', locale, 'editor'], {
      queryParams: {
        supportOrderItemId: this.orderItemId
      }
    });
  }

  // ── Atualizar ─────────────────────────────────────────────

  async onUpdateClick(): Promise<void> {
    if (this.guardLegacyOrder()) return;
    const projectName = this.projectInfo.productName || `#${this.orderItemId}`;
    const confirmed = await this.confirm({
      title: this.translate.instant('SUPPORT_REPAIR.UPDATE_CONFIRM_TITLE'),
      message: this.translate.instant('SUPPORT_REPAIR.UPDATE_CONFIRM_MESSAGE', { project: projectName }),
      icon: 'sync'
    });
    if (!confirmed) return;

    this.busy.set('update');
    try {
      if (!(await this.validateProjectForRepair(this.projectInfo.id))) return;
      await firstValueFrom(this.supportService.syncOrderItemProject(this.orderItemId));
      this.success('SUPPORT_REPAIR.UPDATE_SUCCESS');
    } catch (err) {
      this.failure(err);
    } finally {
      this.busy.set(null);
    }
  }

  // ── Substituir ────────────────────────────────────────────

  async onReplaceClick(): Promise<void> {
    if (this.guardLegacyOrder()) return;
    const ref = this.dialog.open(ProjectPickerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'project-picker-dialog-panel',
      autoFocus: false,
      data: {
        orderItemId: this.orderItemId,
        currentProjectName: this.projectInfo.productName
      } satisfies ProjectPickerDialogData
    });

    const pickedProjectId = await firstValueFrom(ref.afterClosed());
    if (!pickedProjectId || typeof pickedProjectId !== 'number') return;

    this.busy.set('replace');
    try {
      if (!(await this.validateProjectForRepair(pickedProjectId))) return;
      await firstValueFrom(
        this.supportService.swapOrderItemProject(this.orderItemId, pickedProjectId)
      );
      this.success('SUPPORT_REPAIR.REPLACE_SUCCESS');
    } catch (err) {
      this.failure(err);
    } finally {
      this.busy.set(null);
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  /**
   * Legacy orders (apiVersion === 1) were created by the old app and their
   * projects can't be repaired here. Show an info dialog pointing staff to
   * the respective app and signal the caller to abort. Returns true when the
   * action was blocked.
   */
  private guardLegacyOrder(): boolean {
    if (Number(this.apiVersion) !== 1) return false;
    this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      panelClass: 'confirm-dialog-panel',
      data: {
        title: this.translate.instant('SUPPORT_REPAIR.LEGACY_ORDER_TITLE'),
        message: this.translate.instant('SUPPORT_REPAIR.LEGACY_ORDER_MESSAGE'),
        icon: 'info',
        confirmText: this.translate.instant('COMMON.OK'),
        cancelText: null
      }
    });
    return true;
  }

  private async confirm(opts: {
    title: string;
    message: string;
    icon?: string;
    confirmColor?: 'primary' | 'warn';
  }): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      panelClass: 'confirm-dialog-panel',
      data: {
        title: opts.title,
        message: opts.message,
        icon: opts.icon,
        confirmColor: opts.confirmColor ?? 'primary'
      }
    });
    return !!(await firstValueFrom(ref.afterClosed()));
  }

  private async validateProjectForRepair(projectId: number): Promise<boolean> {
    if (!projectId) return true;

    const projectIds = [projectId];
    const validation = await this.uploadResilience.validateForCart(projectIds);
    if (validation.valid) return true;

    this.dialog.open(CartPhotosBlockedDialogComponent, {
      data: { ...validation, projectIds },
      width: '440px',
      panelClass: 'confirm-dialog-panel'
    });
    return false;
  }

  private success(key: string): void {
    this.alertService.success(this.translate.instant(key));
    this.repaired.emit();
  }

  private failure(err: unknown): void {
    console.error('[OrderItemRepairMenu] action failed:', err);
    const key = supportErrorKey(err);
    this.alertService.error(this.translate.instant(key));
  }
}
